/**
 * Timeout Preload Strategy
 *
 * Preloads after a specified delay. Useful for timeout triggers
 * where we want to preload a few seconds before the popup shows.
 */

import type { PreloadStrategy } from './types';
import { createLogger } from '../../shared/logger';

const logger = createLogger('TimeoutPreload');

export class TimeoutPreloadStrategy implements PreloadStrategy {
	private delay: number;
	private timeoutId: ReturnType<typeof setTimeout> | null = null;

	/**
	 * @param delay Milliseconds to wait before preloading
	 */
	constructor(delay: number) {
		logger.log('constructor - delay:', delay, 'ms');
		this.delay = delay;
	}

	/**
	 * Start the timeout
	 */
	public init(onPreload: () => void): void {
		logger.log('init() called, delay:', this.delay);

		if (this.delay <= 0) {
			logger.log('Delay is <= 0, preloading immediately');
			onPreload();
			return;
		}

		this.timeoutId = setTimeout(() => {
			logger.log('Timeout fired! Triggering preload');
			onPreload();
		}, this.delay);
	}

	/**
	 * Cancel the timeout
	 */
	public destroy(): void {
		logger.log('destroy() called');
		if (this.timeoutId !== null) {
			clearTimeout(this.timeoutId);
			this.timeoutId = null;
		}
	}
}
